import React, { useRef, useState } from "react";
import axios from "axios";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { Cookies } from "../../constants/Cookies";
import GlobalEnvironments from "../../modules/globalEnvironment/GlobalEnvironment";
import { util } from "../../config/util";
import styles from "./verificationReport.module.scss";

interface VerificationReportProps {
  results: any[];
  projects: any[];
  onReset: () => void;
  isFromHistory?: boolean;
  isDemo?: boolean;
}

const CATEGORY_LABELS: Record<string, string> = {
  itemize_costs: "일위대가",
  price_calculations: "단가산출",
  material_prices: "자재단가",
  labor_prices: "노임단가",
};

const CATEGORY_DESCRIPTIONS: Record<string, string> = {
  material_prices: "공사에 사용되는 자재의 단가 정보입니다.",
  price_calculations: "복합 단가를 산출하기 위한 계산식입니다.",
  itemize_costs: "공종별 세부 비용을 구성하는 일위대가입니다.",
  labor_prices: "공사에 투입되는 인력의 노임 단가입니다.",
};

const SEVERITY_LABELS: Record<string, string> = {
  high: "중요",
  medium: "확인",
  low: "참고",
};

const GLOSSARY_ITEMS = [
  { term: "품셈 기준데이터", definition: "표준품셈, 물가자료(조달청), 시중노임단가(대한건설협회) 등 국가 공인 데이터베이스에 등록된 데이터입니다." },
  { term: "견적 단가", definition: "공인 데이터에 없어 설계자가 업체 견적서 등을 기준으로 입력한 단가입니다. 2곳 이상 비교견적이 필요합니다." },
  { term: "비교견적", definition: "시중 가격을 확인하기 위해 2곳 이상의 업체에서 받은 가격 견적서입니다." },
  { term: "손료", definition: "공구, 소모품 등의 간접비용으로, 통상 재료비의 일정 비율로 산정합니다." },
  { term: "적용단가", definition: "실제 공사비 산출에 사용되는 최종 단가입니다. 여러 가격 정보 중 선택됩니다." },
  { term: "절사", definition: "도급공사비 등을 일정 단위(천원, 만원 등)로 맞추기 위해 끝자리를 조정하는 것입니다." },
  { term: "노임할증", definition: "도서지역 등 특수 조건에서 노임 단가에 일정 비율을 추가로 적용하는 것입니다." },
];

const VerificationReport: React.FC<VerificationReportProps> = ({
  results,
  projects,
  onReset,
  isFromHistory = false,
  isDemo = false,
}) => {
  const reportRef = useRef<HTMLDivElement>(null);
  const summarySectionRef = useRef<HTMLDivElement>(null);
  const verdictBannerRef = useRef<HTMLDivElement>(null);
  const prioritySectionRef = useRef<HTMLDivElement>(null);
  const settingsSectionRef = useRef<HTMLDivElement>(null);
  const govtSectionRef = useRef<HTMLDivElement>(null);
  const actionSectionRef = useRef<HTMLDivElement>(null);
  const detailSectionRef = useRef<HTMLDivElement>(null);
  const legendSectionRef = useRef<HTMLDivElement>(null);
  const categoryBlockRefs = useRef<(HTMLDivElement | null)[]>([]);

  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfProgress, setPdfProgress] = useState(0);
  const [standardPdfLoading, setStandardPdfLoading] = useState(false);
  const pdfCancelledRef = useRef(false);
  const [isPdfMode, setIsPdfMode] = useState(false);

  const hasDetail = (item: any) => !!(
    (item.guide && item.severity !== "none") ||
    (item.changes && item.changes.length > 0) ||
    (item.content_checks && item.content_checks.length > 0) ||
    (item.added_items && item.added_items.length > 0) ||
    (item.machine_checks && item.machine_checks.length > 0)
  );

  const handleStandardPdfDownload = async () => {
    // 모든 프로젝트의 itemize_costs에서 id > 0인 항목 수집
    const itemIds: number[] = [];
    for (const project of projects) {
      const ics = project.itemize_costs || [];
      for (const ic of ics) {
        if (ic.id > 0) {
          itemIds.push(ic.id);
        }
      }
    }

    if (itemIds.length === 0) {
      alert('표준품셈 참조 데이터가 없습니다.');
      return;
    }

    setStandardPdfLoading(true);
    try {
      const key = util.getCookie(Cookies.authKey);
      const response = await axios.post(
        `${GlobalEnvironments.getApiUrl()}export_excel/standard_pdf_by_ids`,
        { item_ids: itemIds },
        {
          headers: { Authorization: `Token ${key}` },
          responseType: 'blob',
        }
      );

      if (response.data.size > 0) {
        const projectName = results[0]?.project_name || '검증';
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `${projectName}_표준품셈.pdf`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      } else {
        alert('해당 프로젝트에 표준품셈 참조가 없습니다.');
      }
    } catch (err) {
      console.log('표준품셈 PDF 다운로드 오류:', err);
      alert('표준품셈 PDF 다운로드에 실패했습니다.');
    } finally {
      setStandardPdfLoading(false);
    }
  };

  // verdict 데이터 (첫 번째 프로젝트)
  const verdict = results[0]?.verdict;
  const projectSettings = results[0]?.project_settings;
  const governmentMaterials = results[0]?.government_materials;
  const actionItems = results[0]?.action_items as any[] || [];

  // 섹션 번호 동적 계산
  const hasKeyIssues = verdict?.key_issues?.length > 0;
  const hasSettings = !!projectSettings;
  const hasGovtMaterials = governmentMaterials?.items?.length > 0;
  const hasActionItems = actionItems.length > 0;
  let _sectionCounter = 2;
  const settingsNum = hasSettings ? String(_sectionCounter++).padStart(2, "0") : null;
  const govtNum = hasGovtMaterials ? String(_sectionCounter++).padStart(2, "0") : null;
  const priorityNum = hasKeyIssues ? String(_sectionCounter++).padStart(2, "0") : null;
  const detailNum = String(_sectionCounter++).padStart(2, "0");
  const actionNum = hasActionItems ? String(_sectionCounter++).padStart(2, "0") : null;
  const legendNum = String(_sectionCounter++).padStart(2, "0");

  // 전체 통계 계산
  const totalStats = Object.keys(CATEGORY_LABELS).reduce(
    (acc, key) => {
      for (const r of results) {
        const s = r[`${key}_summary`];
        if (s) {
          acc.total += s.total;
          acc.pass += s.pass;
          acc.modified += s.modified;
          acc.needs_review += s.needs_review;
        }
      }
      return acc;
    },
    { total: 0, pass: 0, modified: 0, needs_review: 0 }
  );

  const passRate = totalStats.total > 0
    ? Math.round((totalStats.pass / totalStats.total) * 100)
    : 0;

  // 요소를 캡처하여 캔버스 반환
  const captureElement = async (element: HTMLElement) => {
    return await html2canvas(element, {
      scale: 1.5,
      useCORS: true,
      logging: false,
      backgroundColor: "#ffffff",
    });
  };

  // 캔버스를 JPEG로 변환 (압축)
  const canvasToJpeg = (canvas: HTMLCanvasElement, quality = 0.85): string => {
    return canvas.toDataURL("image/jpeg", quality);
  };

  // 조건부 페이지 넘김: 남은 공간 >= minSpace면 구분선, 아니면 새 페이지
  const conditionalPageBreak = (
    pdf: jsPDF,
    pdfWidth: number,
    pdfHeight: number,
    margin: number,
    currentY: number,
    minSpace: number = 100
  ): number => {
    const remaining = pdfHeight - margin - currentY;
    if (remaining >= minSpace) {
      // 같은 페이지 유지 — 시각적 구분선 추가
      currentY += 12;
      pdf.setDrawColor(210, 210, 210);
      pdf.setLineWidth(0.4);
      pdf.line(margin + 10, currentY, pdfWidth - margin - 10, currentY);
      currentY += 12;
      return currentY;
    } else {
      // 공간 부족 → 새 페이지
      pdf.addPage();
      return margin;
    }
  };

  // 캔버스 슬라이싱: 한 페이지보다 큰 이미지를 페이지 단위로 잘라서 배치
  const addSlicedCanvas = (
    pdf: jsPDF,
    canvas: HTMLCanvasElement,
    pdfWidth: number,
    pdfHeight: number,
    margin: number,
    currentY: number
  ): number => {
    const contentWidth = pdfWidth - margin * 2;
    const usableHeight = pdfHeight - margin * 2;
    const ratio = contentWidth / canvas.width;
    const totalHeightMm = canvas.height * ratio;

    // 페이지에 맞으면 슬라이싱 불필요
    if (currentY + totalHeightMm <= pdfHeight - margin) {
      pdf.addImage(canvasToJpeg(canvas), "JPEG", margin, currentY, contentWidth, totalHeightMm);
      return currentY + totalHeightMm + 2;
    }

    // 슬라이스 높이 (px 단위)
    let srcY = 0;
    const canvasW = canvas.width;
    const canvasH = canvas.height;

    while (srcY < canvasH) {
      // 현재 페이지 남은 공간 (mm)
      const remainMm = pdfHeight - margin - currentY;
      // 남은 공간을 px로 변환
      const sliceHeightPx = Math.min(Math.floor(remainMm / ratio), canvasH - srcY);

      if (sliceHeightPx < 10) {
        // 남은 공간이 너무 작으면 새 페이지
        pdf.addPage();
        currentY = margin;
        continue;
      }

      // 캔버스에서 해당 영역만 잘라내기
      const sliceCanvas = document.createElement("canvas");
      sliceCanvas.width = canvasW;
      sliceCanvas.height = sliceHeightPx;
      const ctx = sliceCanvas.getContext("2d")!;
      ctx.drawImage(canvas, 0, srcY, canvasW, sliceHeightPx, 0, 0, canvasW, sliceHeightPx);

      const sliceHeightMm = sliceHeightPx * ratio;
      pdf.addImage(canvasToJpeg(sliceCanvas), "JPEG", margin, currentY, contentWidth, sliceHeightMm);

      currentY += sliceHeightMm;
      srcY += sliceHeightPx;

      // 아직 남은 내용이 있으면 새 페이지
      if (srcY < canvasH) {
        pdf.addPage();
        currentY = margin;
      }
    }

    return currentY + 2;
  };

  // 단일 요소를 PDF에 추가 (페이지 넘김 + 슬라이싱 자동 처리)
  const addElementToPdf = async (
    pdf: jsPDF,
    element: HTMLElement,
    pdfWidth: number,
    pdfHeight: number,
    margin: number,
    currentY: number
  ): Promise<number | null> => {
    if (pdfCancelledRef.current) return null;

    const contentWidth = pdfWidth - margin * 2;
    const maxPageHeight = pdfHeight - margin * 2;
    const canvas = await captureElement(element);

    if (canvas.height < 5) return currentY;

    const ratio = contentWidth / canvas.width;
    const heightMm = canvas.height * ratio;

    // 요소가 한 페이지보다 크면 → 캔버스 슬라이싱
    if (heightMm > maxPageHeight) {
      return addSlicedCanvas(pdf, canvas, pdfWidth, pdfHeight, margin, currentY);
    }

    if (currentY + heightMm > pdfHeight - margin && currentY > margin + 5) {
      pdf.addPage();
      currentY = margin;
    }

    pdf.addImage(canvasToJpeg(canvas), "JPEG", margin, currentY, contentWidth, heightMm);
    return currentY + heightMm + 2;
  };

  // 카테고리 블록 내 개별 요소들을 순차적으로 PDF에 추가
  const addCategoryBlockToPdf = async (
    pdf: jsPDF,
    block: HTMLElement,
    pdfWidth: number,
    pdfHeight: number,
    margin: number,
    startY: number
  ): Promise<number | null> => {
    let currentY: number | null = startY;

    const header = block.querySelector(`.${styles.categoryHeader}`) as HTMLElement;
    if (header) {
      currentY = await addElementToPdf(pdf, header, pdfWidth, pdfHeight, margin, currentY);
      if (currentY === null) return null;
    }

    const issueTable = block.querySelector(`.${styles.issueTable}`) as HTMLElement;
    if (issueTable) {
      // issueTable의 모든 직접 자식을 순회 (tableHeader, tableRow, machineChecksFullWidth 등)
      for (const child of issueTable.children) {
        if (pdfCancelledRef.current) return null;
        const el = child as HTMLElement;
        if (!el || el.offsetHeight < 2) continue;
        currentY = await addElementToPdf(pdf, el, pdfWidth, pdfHeight, margin, currentY!);
        if (currentY === null) return null;
      }
    }

    // passedSummary/emptyState는 카테고리 마지막 요약 — 새 페이지로 넘기지 않고 이전 내용에 붙임
    const tail = block.querySelector(`.${styles.passedSummary}`) as HTMLElement
      || block.querySelector(`.${styles.emptyState}`) as HTMLElement;
    if (tail && currentY !== null) {
      if (pdfCancelledRef.current) return null;
      const contentWidth = pdfWidth - margin * 2;
      const canvas = await captureElement(tail);
      if (canvas.height >= 5) {
        const ratio = contentWidth / canvas.width;
        const heightMm = canvas.height * ratio;
        // 페이지 넘김 없이 현재 위치에 강제 배치
        pdf.addImage(canvasToJpeg(canvas), "JPEG", margin, currentY, contentWidth, heightMm);
        currentY += heightMm + 2;
      }
    }

    return currentY! + 3;
  };

  // 단일 요소를 캡처하고 PDF에 배치 (orphan 방지 + 페이지 넘김 + 슬라이싱)
  const placeChildElement = async (
    pdf: jsPDF,
    child: HTMLElement,
    pdfWidth: number,
    pdfHeight: number,
    margin: number,
    currentY: number,
    hasMoreSiblings: boolean
  ): Promise<number | null> => {
    if (pdfCancelledRef.current) return null;
    const contentWidth = pdfWidth - margin * 2;
    const maxPageHeight = pdfHeight - margin * 2;

    const canvas = await captureElement(child);
    if (canvas.height < 5) return currentY;

    const ratio = contentWidth / canvas.width;
    const heightMm = canvas.height * ratio;

    // 요소가 한 페이지보다 크면 → 자식으로 분해하여 재귀 처리
    if (heightMm > maxPageHeight * 0.85 && child.children.length > 1) {
      for (let j = 0; j < child.children.length; j++) {
        const sub = child.children[j] as HTMLElement;
        if (!sub || sub.offsetHeight < 2) continue;
        const hasSiblings = j < child.children.length - 1 || hasMoreSiblings;
        const y = await placeChildElement(pdf, sub, pdfWidth, pdfHeight, margin, currentY, hasSiblings);
        if (y === null) return null;
        currentY = y;
      }
      return currentY;
    }

    // 자식이 없거나 1개인데 여전히 페이지보다 크면 → 캔버스 슬라이싱
    if (heightMm > maxPageHeight) {
      return addSlicedCanvas(pdf, canvas, pdfWidth, pdfHeight, margin, currentY);
    }

    // Orphan 방지: 작은 요소(제목 등)가 페이지 하단에 혼자 남지 않도록
    const fitsOnPage = currentY + heightMm <= pdfHeight - margin;
    if (fitsOnPage && heightMm < 30 && hasMoreSiblings) {
      const remainingAfter = pdfHeight - margin - (currentY + heightMm);
      if (remainingAfter < 90 && currentY > margin + 5) {
        pdf.addPage();
        currentY = margin;
      }
    }

    // 페이지 넘김: 요소가 현재 페이지에 안 맞으면 새 페이지
    if (currentY + heightMm > pdfHeight - margin && currentY > margin + 5) {
      pdf.addPage();
      currentY = margin;
    }

    pdf.addImage(canvasToJpeg(canvas), "JPEG", margin, currentY, contentWidth, heightMm);
    return currentY + heightMm + 2;
  };

  // 섹션 내 자식 요소들을 개별적으로 PDF에 추가 (짤림 방지 + orphan 방지 + 자동 분해)
  const addChildrenToPdf = async (
    pdf: jsPDF,
    parent: HTMLElement,
    pdfWidth: number,
    pdfHeight: number,
    margin: number,
    startY: number
  ): Promise<number | null> => {
    let currentY = startY;
    const children = parent.children;
    for (let i = 0; i < children.length; i++) {
      const child = children[i] as HTMLElement;
      if (!child || child.offsetHeight < 2) continue;
      const hasMore = i < children.length - 1;
      const y = await placeChildElement(pdf, child, pdfWidth, pdfHeight, margin, currentY, hasMore);
      if (y === null) return null;
      currentY = y;
    }
    return currentY;
  };

  // PDF 표지 생성
  const addCoverPage = (pdf: jsPDF, projectName: string) => {
    const w = pdf.internal.pageSize.getWidth();
    const h = pdf.internal.pageSize.getHeight();

    // 배경 상단 바
    pdf.setFillColor(30, 58, 95); // $color-primary #1e3a5f
    pdf.rect(0, 0, w, 6, "F");

    // 하단 바
    pdf.setFillColor(30, 58, 95);
    pdf.rect(0, h - 6, w, 6, "F");

    // 중앙 콘텐츠
    const centerX = w / 2;

    // "CONAX" 브랜드
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(14);
    pdf.setTextColor(30, 58, 95);
    pdf.text("CONAX", centerX, 80, { align: "center" });

    // 구분선
    pdf.setDrawColor(30, 58, 95);
    pdf.setLineWidth(0.5);
    pdf.line(centerX - 30, 87, centerX + 30, 87);

    // 메인 타이틀 (한글 — 기본 폰트로 표현 불가하므로 영문+한글 혼합)
    // jsPDF 기본 폰트는 한글 미지원 → html2canvas로 표지 요소를 캡처하는 방식 대신
    // 숨겨진 DOM 요소를 만들어 캡처
    return { centerX, w, h };
  };

  // PDF 다운로드
  const handlePdfDownload = async () => {
    if (!summarySectionRef.current || !legendSectionRef.current) return;

    setPdfLoading(true);
    setPdfProgress(0);
    pdfCancelledRef.current = false;
    setIsPdfMode(true);
    await new Promise((r) => setTimeout(r, 100));

    try {
      const pdf = new jsPDF({
        orientation: "portrait",
        unit: "mm",
        format: "a4",
      });

      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const margin = 10;
      const contentWidth = pdfWidth - margin * 2;

      // 0. 표지 페이지 — 숨겨진 DOM 요소를 캡처
      setPdfProgress(2);
      if (pdfCancelledRef.current) return;

      const projectName = results[0]?.project_name || "프로젝트";
      const projectType = results[0]?.project_settings?.construction_info?.construction_type || "";
      const today = new Date();
      const dateStr = `${today.getFullYear()}년 ${today.getMonth() + 1}월 ${today.getDate()}일`;

      // 표지용 임시 DOM 생성
      const coverEl = document.createElement("div");
      coverEl.style.cssText = "position:fixed;left:-9999px;top:0;width:800px;background:#fff;padding:0;";
      coverEl.innerHTML = `
        <div style="position:relative;min-height:1060px;font-family:'Pretendard',sans-serif;background:#fff;overflow:hidden;">
          <!-- 좌측 네이비 사이드바 -->
          <div style="position:absolute;left:0;top:0;bottom:0;width:90px;background:#1e3a5f;"></div>
          <!-- 사이드바 안 세로 텍스트 -->
          <div style="position:absolute;left:0;top:0;bottom:0;width:90px;display:flex;align-items:center;justify-content:center;">
            <div style="writing-mode:vertical-rl;text-orientation:mixed;font-size:13px;font-weight:600;color:rgba(255,255,255,0.4);letter-spacing:8px;">CONAX VERIFICATION REPORT</div>
          </div>
          <!-- 사이드바 하단 로고 -->
          <div style="position:absolute;left:0;bottom:40px;width:90px;display:flex;align-items:center;justify-content:center;">
            <div style="font-size:11px;font-weight:800;color:rgba(255,255,255,0.6);letter-spacing:3px;">CONAX</div>
          </div>
          <!-- 메인 콘텐츠 -->
          <div style="margin-left:90px;padding:80px 60px 60px;display:flex;flex-direction:column;min-height:1060px;">
            <!-- 상단: 문서 종류 -->
            <div style="font-size:13px;font-weight:600;color:#9ca3af;letter-spacing:6px;margin-bottom:60px;">DESIGN COST REVIEW</div>
            <!-- 중앙: 메인 타이틀 -->
            <div style="flex:1;display:flex;flex-direction:column;justify-content:center;">
              <div style="font-size:48px;font-weight:900;color:#1e3a5f;line-height:1.25;margin-bottom:24px;">설계내역<br/>검토 보고서</div>
              <div style="width:60px;height:4px;background:#1e3a5f;margin-bottom:36px;border-radius:2px;"></div>
              <div style="font-size:24px;font-weight:700;color:#111827;margin-bottom:8px;">${projectName}</div>
              ${projectType ? `<div style="font-size:15px;font-weight:500;color:#6b7280;">${projectType}</div>` : ""}
            </div>
            <!-- 하단: 정보 -->
            <div style="border-top:1px solid #e5e7eb;padding-top:24px;display:flex;justify-content:space-between;align-items:flex-end;">
              <div>
                <div style="font-size:13px;color:#9ca3af;margin-bottom:4px;">검토일자</div>
                <div style="font-size:15px;font-weight:600;color:#374151;">${dateStr}</div>
              </div>
              <div style="text-align:right;">
                <div style="font-size:13px;color:#9ca3af;margin-bottom:4px;">검토 시스템</div>
                <div style="font-size:15px;font-weight:600;color:#1e3a5f;">콘엑스 AI 자동 검증</div>
                <div style="font-size:12px;color:#9ca3af;margin-top:2px;">www.ddusul.com</div>
              </div>
            </div>
          </div>
        </div>
      `;
      document.body.appendChild(coverEl);

      const coverCanvas = await captureElement(coverEl);
      document.body.removeChild(coverEl);
      if (pdfCancelledRef.current) return;

      const coverRatio = contentWidth / coverCanvas.width;
      const coverHeightMm = coverCanvas.height * coverRatio;
      const coverY = Math.max(0, (pdfHeight - coverHeightMm) / 2);
      pdf.addImage(canvasToJpeg(coverCanvas, 0.92), "JPEG", margin, coverY, contentWidth, coverHeightMm);

      // 1. 종합 결과 섹션 (자식 요소별 분할 배치)
      pdf.addPage();
      setPdfProgress(5);
      if (pdfCancelledRef.current) return;
      let lastY = margin;
      const summaryY = await addChildrenToPdf(pdf, summarySectionRef.current, pdfWidth, pdfHeight, margin, lastY);
      if (summaryY === null) return;
      lastY = summaryY;
      setPdfProgress(10);

      // 1b. Verdict 배너
      if (verdictBannerRef.current) {
        if (pdfCancelledRef.current) return;
        const verdictY = await addElementToPdf(pdf, verdictBannerRef.current, pdfWidth, pdfHeight, margin, lastY);
        if (verdictY === null) return;
        lastY = verdictY;
      }
      setPdfProgress(15);

      // 2. 프로젝트 설정 검증 섹션 — 조건부 페이지 넘김
      if (settingsSectionRef.current) {
        if (pdfCancelledRef.current) return;
        lastY = conditionalPageBreak(pdf, pdfWidth, pdfHeight, margin, lastY, 150);
        const settingsY = await addChildrenToPdf(pdf, settingsSectionRef.current, pdfWidth, pdfHeight, margin, lastY);
        if (settingsY === null) return;
        lastY = settingsY;
      }
      setPdfProgress(16);

      // 2b. 관급자재 검증 섹션 — 조건부 페이지 넘김
      if (govtSectionRef.current) {
        if (pdfCancelledRef.current) return;
        lastY = conditionalPageBreak(pdf, pdfWidth, pdfHeight, margin, lastY, 150);
        const govtY = await addChildrenToPdf(pdf, govtSectionRef.current, pdfWidth, pdfHeight, margin, lastY);
        if (govtY === null) return;
        lastY = govtY;
      }
      setPdfProgress(18);

      // 3. 핵심 확인 사항 섹션 — 조건부 페이지 넘김
      if (prioritySectionRef.current) {
        if (pdfCancelledRef.current) return;
        lastY = conditionalPageBreak(pdf, pdfWidth, pdfHeight, margin, lastY, 150);
        const priorityY = await addChildrenToPdf(pdf, prioritySectionRef.current, pdfWidth, pdfHeight, margin, lastY);
        if (priorityY === null) return;
        lastY = priorityY;
      }
      setPdfProgress(22);

      // 4. 상세 검증 결과 섹션 (새 페이지)
      pdf.addPage();

      const detailTitle = detailSectionRef.current?.querySelector("h2");
      let currentY = margin;
      if (detailTitle) {
        if (pdfCancelledRef.current) return;
        const titleCanvas = await captureElement(detailTitle as HTMLElement);
        if (pdfCancelledRef.current) return;
        const titleRatio = contentWidth / titleCanvas.width;
        const titleHeightMm = titleCanvas.height * titleRatio;
        pdf.addImage(
          canvasToJpeg(titleCanvas),
          "JPEG",
          margin,
          currentY,
          contentWidth,
          titleHeightMm
        );
        currentY += titleHeightMm + 5;
      }
      setPdfProgress(25);

      const validBlocks = categoryBlockRefs.current.filter((b): b is HTMLDivElement => b !== null);
      lastY = currentY;
      for (let i = 0; i < validBlocks.length; i++) {
        if (pdfCancelledRef.current) return;
        // 두 번째 카테고리부터 조건부 페이지 넘김
        if (i > 0) {
          lastY = conditionalPageBreak(pdf, pdfWidth, pdfHeight, margin, lastY, 140);
        }
        const blockY = await addCategoryBlockToPdf(pdf, validBlocks[i], pdfWidth, pdfHeight, margin, lastY);
        if (blockY === null) return;
        lastY = blockY;
        setPdfProgress(25 + Math.round((i + 1) / validBlocks.length * 55));
      }

      // 4b. 조치 요청 사항 섹션 — 조건부 페이지 넘김
      if (actionSectionRef.current) {
        if (pdfCancelledRef.current) return;
        lastY = conditionalPageBreak(pdf, pdfWidth, pdfHeight, margin, lastY, 150);
        const actionY = await addChildrenToPdf(pdf, actionSectionRef.current, pdfWidth, pdfHeight, margin, lastY);
        if (actionY === null) return;
        lastY = actionY;
      }

      // 5. 범례 섹션 — 조건부 페이지 넘김
      if (pdfCancelledRef.current) return;
      setPdfProgress(85);
      const legendCanvas = await captureElement(legendSectionRef.current);
      if (pdfCancelledRef.current) return;
      const legendRatio = contentWidth / legendCanvas.width;
      const legendHeightMm = legendCanvas.height * legendRatio;
      const legendRemaining = pdfHeight - margin - lastY;
      if (legendRemaining >= legendHeightMm + 10) {
        // 같은 페이지에 맞으면 구분선 후 배치
        lastY = conditionalPageBreak(pdf, pdfWidth, pdfHeight, margin, lastY, legendHeightMm + 30);
        pdf.addImage(canvasToJpeg(legendCanvas), "JPEG", margin, lastY, contentWidth, legendHeightMm);
      } else {
        // 안 맞으면 새 페이지
        pdf.addPage();
        pdf.addImage(canvasToJpeg(legendCanvas), "JPEG", margin, margin, contentWidth, legendHeightMm);
      }

      if (pdfCancelledRef.current) return;
      setPdfProgress(100);
      pdf.save(`검증보고서_${projectName}_${new Date().toISOString().slice(0, 10)}.pdf`);
    } catch (error) {
      alert("PDF 생성 중 오류가 발생했습니다.");
      console.error(error);
    } finally {
      setIsPdfMode(false);
      setPdfLoading(false);
      setPdfProgress(0);
    }
  };

  const formatValue = (val: any): string => {
    if (val === null || val === undefined || val === "") return "-";
    if (typeof val === "number") return val.toLocaleString();
    return String(val);
  };

  const getStatusClass = (status: string) => {
    if (status === "pass") return styles.statusPass;
    if (status === "modified") return styles.statusModified;
    return styles.statusReview;
  };

  const getSeverityClass = (severity: string) => {
    if (severity === "high") return styles.severityHigh;
    if (severity === "medium") return styles.severityMedium;
    if (severity === "low") return styles.severityLow;
    return "";
  };

  // 2-상태 뱃지: 적합(초록) / 확인필요(빨강)
  const getActionBadge = (item: any): { label: string; className: string } => {
    if (item.status === "pass") {
      return { label: "적합", className: styles.statusPass };
    }
    return { label: "확인필요", className: styles.statusIssue };
  };

  const getVerdictClass = (level: string) => {
    return level === "good" ? styles.verdictGood : styles.verdictWarning;
  };

  const getVerdictIcon = (level: string) => {
    return level === "good" ? "✓" : "!";
  };

  const getVerdictLabel = (level: string) => {
    return level === "good" ? "적합" : "확인필요";
  };

  const currentDate = new Date().toLocaleDateString("ko-KR", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  return (
    <div className={styles.reportWrapper}>
      {/* PDF 생성 로딩 오버레이 */}
      {pdfLoading && (
        <div className={styles.pdfLoadingOverlay}>
          <div className={styles.pdfLoadingModal}>
            <div className={styles.pdfLoadingSpinner} />
            <div className={styles.pdfLoadingText}>PDF 생성 중...</div>
            <div className={styles.pdfProgressBar}>
              <div
                className={styles.pdfProgressFill}
                style={{ width: `${pdfProgress}%` }}
              />
            </div>
            <div className={styles.pdfProgressText}>{pdfProgress}%</div>
            <button
              className={styles.pdfCancelBtn}
              onClick={() => {
                pdfCancelledRef.current = true;
                setPdfLoading(false);
                setPdfProgress(0);
              }}
            >
              취소
            </button>
          </div>
        </div>
      )}

      {/* 데모 배너 */}
      {isDemo && (
        <div className={styles.demoBanner}>
          이 보고서는 샘플입니다. 검증 권한 승인 후 직접 검증을 진행할 수 있습니다.
        </div>
      )}

      {/* 액션 버튼 영역 */}
      <div className={styles.actionBar}>
        <div className={styles.actionBarInner}>
          <button className={styles.btnSecondary} onClick={onReset} disabled={pdfLoading}>
            {isDemo ? "돌아가기" : isFromHistory ? "대시보드" : "새 검증"}
          </button>
          {!isDemo && (
            <div className={styles.actionRight}>
              <button
                className={styles.btnOutline}
                onClick={handleStandardPdfDownload}
                disabled={pdfLoading || standardPdfLoading}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/>
                  <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/>
                </svg>
                {standardPdfLoading ? '다운로드 중...' : '표준품셈'}
              </button>
              <button
                className={styles.btnPrimary}
                onClick={handlePdfDownload}
                disabled={pdfLoading}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                  <polyline points="7 10 12 15 17 10"/>
                  <line x1="12" y1="15" x2="12" y2="3"/>
                </svg>
                PDF 다운로드
              </button>
            </div>
          )}
        </div>
      </div>

      {/* 보고서 본문 */}
      <div ref={reportRef} className={styles.reportDocument}>
        {/* 종합 결과 섹션 (헤더 포함) */}
        <div ref={summarySectionRef}>
          {/* 보고서 헤더 */}
          <header className={styles.reportHeader}>
            <div className={styles.headerTop}>
              <span className={styles.reportLabel}>설계내역 검증 보고서</span>
              <span className={styles.reportDate}>{currentDate}</span>
            </div>
            <h1 className={styles.reportTitle}>
              {results[0]?.project_name || "프로젝트 검증 결과"}
            </h1>
            <p className={styles.reportSubtitle}>
              본 보고서는 제출된 설계내역의 데이터 무결성을 검증한 결과입니다.
            </p>
          </header>

          {/* 종합 결과 섹션 */}
          <section className={styles.summarySection}>
          <h2 className={styles.sectionTitle}>
            <span className={styles.sectionNumber}>01</span>
            종합 검증 결과
          </h2>

          <div className={styles.overallResult}>
            <div className={styles.scoreCircle}>
              <div className={styles.scoreRingWrap}>
                <svg viewBox="0 0 100 100" className={styles.scoreRing}>
                  <circle
                    cx="50"
                    cy="50"
                    r="45"
                    fill="none"
                    stroke="#fecaca"
                    strokeWidth="8"
                  />
                  <circle
                    cx="50"
                    cy="50"
                    r="45"
                    fill="none"
                    stroke="#059669"
                    strokeWidth="8"
                    strokeLinecap="round"
                    strokeDasharray={`${passRate * 2.83} 283`}
                    transform="rotate(-90 50 50)"
                  />
                </svg>
                <div className={styles.scoreValue}>
                  <span className={styles.scoreNumber}>{passRate}</span>
                  <span className={styles.scoreUnit}>%</span>
                </div>
              </div>
              <div className={styles.scoreLabel}>적합률 · {totalStats.total}개 항목</div>
            </div>

            <div className={styles.statsGrid}>
              <div className={`${styles.statItem} ${styles.statPass}`}>
                <div className={styles.statValue}>{totalStats.pass.toLocaleString()}</div>
                <div className={styles.statLabel}>적합</div>
              </div>
              <div className={`${styles.statItem} ${styles.statIssue}`}>
                <div className={styles.statValue}>{(totalStats.total - totalStats.pass).toLocaleString()}</div>
                <div className={styles.statLabel}>확인필요</div>
              </div>
            </div>
          </div>

          {/* 카테고리별 요약 바 */}
          <div className={styles.categoryBars}>
            {Object.entries(CATEGORY_LABELS).map(([key, label]) => {
              let catTotal = 0, catPass = 0;
              for (const r of results) {
                const s = r[`${key}_summary`];
                if (s) {
                  catTotal += s.total;
                  catPass += s.pass;
                }
              }
              const catPassRate = catTotal > 0 ? (catPass / catTotal) * 100 : 0;
              const catIssue = catTotal - catPass;

              return (
                <div key={key} className={styles.categoryBar}>
                  <div className={styles.categoryBarHeader}>
                    <span className={styles.categoryBarName}>{label}</span>
                    <span className={styles.categoryBarStats}>
                      {catPass}/{catTotal} 적합
                    </span>
                  </div>
                  <div className={styles.progressBar}>
                    <div
                      className={styles.progressFill}
                      style={{ width: `${catPassRate}%` }}
                    />
                  </div>
                  {catIssue > 0 && (
                    <div className={styles.categoryBarLegend}>
                      <span className={styles.legendIssue}>{catIssue} 확인필요</span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          </section>
        </div>

        {/* 총평 배너 */}
        {verdict && (
          <div ref={verdictBannerRef} className={`${styles.verdictBanner} ${getVerdictClass(verdict.level)}`}>
            <div className={styles.verdictIcon}>{getVerdictIcon(verdict.level)}</div>
            <div className={styles.verdictContent}>
              <div className={styles.verdictLabel}>{getVerdictLabel(verdict.level)}</div>
              <div className={styles.verdictSummary}>{verdict.summary}</div>
            </div>
          </div>
        )}

        {/* 프로젝트 설정 검증 섹션 */}
        {projectSettings && (
          <section ref={settingsSectionRef} className={styles.settingsSection}>
            <h2 className={styles.sectionTitle}>
              <span className={styles.sectionNumber}>{settingsNum}</span>
              프로젝트 설정 검증
            </h2>

            {/* 공사 기본정보 + 세부설정 */}
            <div className={styles.settingsCard}>
              <h3 className={styles.settingsCardTitle}>원가계산서 기본설정</h3>
              <div className={styles.settingsGrid}>
                <div className={styles.settingsItem}>
                  <span className={styles.settingsLabel}>공사종류</span>
                  <span className={styles.settingsValue}>
                    {projectSettings.construction_info?.type_label || "미설정"}
                  </span>
                </div>
                <div className={styles.settingsItem}>
                  <span className={styles.settingsLabel}>공사일수</span>
                  <span className={styles.settingsValue}>
                    {projectSettings.construction_info?.duration
                      ? `${Number(projectSettings.construction_info.duration).toLocaleString()}일`
                      : "미설정"}
                  </span>
                </div>
                {projectSettings.construction_info?.settings?.map((s: any, idx: number) => (
                  <div key={idx} className={styles.settingsItem}>
                    <span className={styles.settingsLabel}>{s.label}</span>
                    <span className={styles.settingsValue}>{s.value}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* 원가계산서 요율 */}
            {projectSettings.rates?.length > 0 && (
              <div className={styles.settingsCard}>
                <h3 className={styles.settingsCardTitle}>원가계산서 요율</h3>
                {!projectSettings.flag_calculated && !projectSettings.rates?.some((r: any) => r.rate && parseFloat(r.rate) > 0) && (
                  <div className={styles.cuttingAlert}>
                    <span>ℹ</span>
                    원가계산서 요율이 설정되지 않았습니다. 뚜껑에서 원가계산서 페이지에 진입하면 자동으로 요율이 계산됩니다.
                  </div>
                )}
                <div className={styles.rateTable}>
                  <div className={styles.rateTableHeader}>
                    <span>항목</span>
                    <span style={{textAlign: 'right'}}>요율</span>
                    <span style={{textAlign: 'right'}}>적용일자</span>
                  </div>
                  {projectSettings.rates
                    .filter((rate: any) => projectSettings.flag_calculated ? true : rate.rate !== 0)
                    .map((rate: any, idx: number) => (
                    <div key={idx} className={styles.rateRow}>
                      <span className={styles.rateName}>
                        {rate.name}
                        {rate.standard_range && (
                          <span className={styles.standardRange}>
                            (기준 {rate.standard_range.min}~{rate.standard_range.max}%)
                          </span>
                        )}
                      </span>
                      <span className={styles.rateValue}>
                        {rate.rate}%
                      </span>
                      <span className={styles.rateApplyDate}>
                        {rate.apply_date || "-"}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* 절사 정보 */}
            {projectSettings.cutting?.adjusted_items?.length > 0 && (
              <div className={styles.settingsCard}>
                <h3 className={styles.settingsCardTitle}>절사 / 금액조정</h3>
                {projectSettings.cutting.do_cutting_unit > 0 && (
                  <div className={styles.cuttingAlert}>
                    <span>⚠</span>
                    도급공사비 {Number(projectSettings.cutting.do_cutting_unit).toLocaleString()}원 단위 절사 적용
                  </div>
                )}
                {projectSettings.cutting.adjusted_items.map((adj: any, idx: number) => (
                  <div key={idx} className={styles.adjustedItem}>
                    {adj.cutting_amount != null ? (
                      <>
                        <span className={`${styles.severityBadge} ${styles.severityMedium}`}>절사</span>
                        <span className={styles.adjustedItemText}>
                          {adj.title}에서 {Number(adj.cutting_amount).toLocaleString()}원 절사 조정
                          {adj.cutting_expr && <span style={{color: '#888', marginLeft: 4}}>({adj.cutting_expr})</span>}
                        </span>
                      </>
                    ) : adj.formula_adjustment != null ? (
                      <>
                        <span className={`${styles.severityBadge} ${styles.severityMedium}`}>수식조정</span>
                        <span className={styles.adjustedItemText}>
                          {adj.title} 산출근거에 {adj.formula_adjustment > 0 ? '+' : ''}{Number(adj.formula_adjustment).toLocaleString()}원 금액조정
                          {adj.formula && <span style={{color: '#888', marginLeft: 4}}>({adj.formula})</span>}
                        </span>
                      </>
                    ) : (
                      <>
                        <span className={`${styles.severityBadge} ${styles.severityHigh}`}>수동</span>
                        <span className={styles.adjustedItemText}>
                          {adj.title} → {Number(adj.absolute_value).toLocaleString()}원 수동 설정
                        </span>
                      </>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* 사용자 추가 항목 + 직접공사비 체크 */}
            {projectSettings.custom_invoice_check && (
              <div className={styles.settingsCard}>
                <h3 className={styles.settingsCardTitle}>원가계산서 추가 항목</h3>
                <div className={styles.customInvoiceSection}>
                  <div className={styles.customInvoiceLabel}>사용자 추가 항목</div>
                  <div className={styles.customInvoiceItems}>
                    {projectSettings.custom_invoice_check.custom_items.map((ci: any, idx: number) => (
                      <span key={idx} className={styles.customInvoiceTag}>
                        {ci.title} ({ci.letter})
                        {ci.value ? ` ${Number(ci.value).toLocaleString()}원` : ''}
                      </span>
                    ))}
                  </div>
                </div>
                <div className={styles.customInvoiceLabel} style={{marginTop: 12}}>
                  내역서 대분류 - 직접공사비 포함 상태
                </div>
                <table className={styles.laborCompareTable}>
                  <thead>
                    <tr>
                      <th style={{textAlign: 'left'}}>대분류 (공사)</th>
                      <th>직접공사비</th>
                      <th>판정</th>
                    </tr>
                  </thead>
                  <tbody>
                    {projectSettings.custom_invoice_check.corporations.map((corp: any, idx: number) => (
                      <tr key={idx}>
                        <td className={styles.laborFieldCol}>{corp.name}</td>
                        <td style={{textAlign: 'center'}}>
                          {corp.included_in_direct ? '포함' : '제외'}
                        </td>
                        <td style={{textAlign: 'center'}}>
                          {corp.included_in_direct
                            ? <span className={styles.statusIssue} style={{fontSize: 11, padding: '2px 8px'}}>확인필요</span>
                            : <span className={styles.statusPass} style={{fontSize: 11, padding: '2px 8px'}}>정상</span>
                          }
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {projectSettings.custom_invoice_check.excluded_count === 0 && (
                  <div className={styles.cuttingAlert} style={{marginTop: 8}}>
                    <span>⚠</span>
                    원가계산서에 추가 항목이 있으나, 모든 대분류가 직접공사비에 포함되어 있어 이중산출 우려가 있습니다.
                  </div>
                )}
                {projectSettings.custom_invoice_check.excluded_count > 0 &&
                 projectSettings.custom_invoice_check.included_count > 0 && (
                  <p style={{fontSize: 11, color: '#78716c', marginTop: 8, lineHeight: 1.5}}>
                    원가계산서에 추가 항목이 있는 경우, 해당 대분류의 직접공사비 포함 체크를 해제해야 이중산출이 방지됩니다.
                    직접공사비가 제외된 대분류는 원가계산서에서 별도로 산출됩니다.
                  </p>
                )}
              </div>
            )}

            {/* 노임단가 정보 */}
            <div className={styles.settingsCard}>
              <h3 className={styles.settingsCardTitle}>노임단가</h3>
              <div className={styles.settingsGrid}>
                <div className={styles.settingsItem}>
                  <span className={styles.settingsLabel}>적용일자</span>
                  <span className={styles.settingsValue}>
                    {projectSettings.labor_surcharge?.apply_date || "미설정"}
                  </span>
                </div>
                <div className={styles.settingsItem}>
                  <span className={styles.settingsLabel}>DB 기준일자</span>
                  <span className={styles.settingsValue}>
                    {projectSettings.labor_surcharge?.db_apply_date || "-"}
                  </span>
                </div>
                <div className={styles.settingsItem}>
                  <span className={styles.settingsLabel}>노임 항목 수</span>
                  <span className={styles.settingsValue}>
                    {projectSettings.labor_surcharge?.total_count || 0}개
                  </span>
                </div>
              </div>
              <div className={`${styles.surchargeInfo} ${
                projectSettings.labor_surcharge?.enabled
                  ? styles.surchargeEnabled
                  : styles.surchargeDisabled
              }`}>
                {projectSettings.labor_surcharge?.enabled
                  ? `노임할증 ${projectSettings.labor_surcharge.rate}% 적용 (${projectSettings.labor_surcharge.applied_count}/${projectSettings.labor_surcharge.total_count}개)`
                  : "노임할증 미적용"
                }
              </div>
            </div>
          </section>
        )}

        {/* 관급자재 검증 섹션 */}
        {hasGovtMaterials && (() => {
          const summary = governmentMaterials.summary;
          const allItems = governmentMaterials.items as any[];
          const issueItems = allItems.filter((i: any) => i.severity !== "none");
          const passItems = allItems.filter((i: any) => i.severity === "none");

          const getGovtStatusBadge = (item: any) => {
            if (item.status === "category_mismatch") return { label: "분류 오류", cls: styles.severityHigh };
            if (item.status === "no_product_id") return { label: "정보 미비", cls: styles.severityMedium };
            if (item.status === "no_db_match") return { label: "미매칭", cls: styles.severityMedium };
            if (item.status === "truncated") return { label: "절사 적용", cls: styles.severityLow };
            if (item.status === "price_mismatch") return { label: "확인 필요", cls: styles.severityHigh };
            return { label: "적합", cls: styles.statusPass };
          };

          return (
            <section ref={govtSectionRef} className={styles.settingsSection}>
              <h2 className={styles.sectionTitle}>
                <span className={styles.sectionNumber}>{govtNum}</span>
                관급자재 검증
              </h2>

              {/* 현황 카드 */}
              <div className={styles.settingsCard}>
                <h3 className={styles.settingsCardTitle}>관급자재 현황</h3>
                <div className={styles.settingsGrid}>
                  <div className={styles.settingsItem}>
                    <span className={styles.settingsLabel}>전체</span>
                    <span className={styles.settingsValue}>{summary.total}개</span>
                  </div>
                  <div className={styles.settingsItem}>
                    <span className={styles.settingsLabel}>도급자관급</span>
                    <span className={styles.settingsValue}>{summary.do_count}개</span>
                  </div>
                  <div className={styles.settingsItem}>
                    <span className={styles.settingsLabel}>관급자관급</span>
                    <span className={styles.settingsValue}>{summary.guan_count}개</span>
                  </div>
                  <div className={styles.settingsItem}>
                    <span className={styles.settingsLabel}>적합</span>
                    <span className={styles.settingsValue}>{summary.pass}개</span>
                  </div>
                </div>
              </div>

              {/* 인도조건 분류 기준 안내 */}
              <div className={styles.settingsCard}>
                <h3 className={styles.settingsCardTitle}>인도조건별 분류 기준</h3>
                <div className={styles.reasonOneLiner} style={{ lineHeight: "1.8", padding: "4px 0" }}>
                  <strong>현장설치도</strong>: 시공업체가 설치까지 책임 → <strong>관급자관급</strong>에 해당<br />
                  <strong>납품장소 하차도</strong>: 현장 하차만 수행 → <strong>도급자관급</strong>에 해당
                </div>
              </div>

              {/* 확인 필요 항목 테이블 */}
              {issueItems.length > 0 && (
                <div className={styles.settingsCard}>
                  <h3 className={styles.settingsCardTitle}>확인 필요 항목</h3>
                  <div className={styles.issueTable}>
                    <div className={styles.tableHeader}>
                      <div className={styles.colName}>품명</div>
                      <div className={styles.colPrice}>적용단가</div>
                      <div className={styles.colStatus}>검증</div>
                      <div className={styles.colReason}>검증결과 / 인도조건</div>
                    </div>
                    {issueItems.map((item: any, idx: number) => {
                      const badge = getGovtStatusBadge(item);
                      return (
                        <div key={idx} className={styles.tableRow}>
                          <div className={styles.colName}>
                            {item.name || "(이름 없음)"}
                            {item.standard && (
                              <div className={styles.itemStandard}>{item.standard}</div>
                            )}
                            {item.product_id && (
                              <div className={styles.itemDivision}>식별번호: {item.product_id}</div>
                            )}
                            <span className={styles.govtTag}>{item.category}</span>
                          </div>
                          <div className={styles.colPrice}>
                            {item.apply_price ? Number(item.apply_price).toLocaleString() : "-"}
                            {item.db_price != null && item.db_price > 0 && (
                              <div className={styles.itemStandard}>DB: {Number(item.db_price).toLocaleString()}</div>
                            )}
                          </div>
                          <div className={styles.colStatus}>
                            <span className={badge.cls}>{badge.label}</span>
                          </div>
                          <div className={styles.colReason}>
                            <span className={styles.reasonOneLiner}>{item.description}</span>
                            {item.delivery_condition && (
                              <div className={styles.itemDivision}>인도조건: {item.delivery_condition}</div>
                            )}
                            {item.checks?.some((c: any) => c.type === "계약기간") && (
                              <div className={styles.itemDivision}>계약 만료됨</div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* 적합 항목 테이블 (인도조건 표시) */}
              {passItems.length > 0 && (
                <div className={styles.settingsCard}>
                  <h3 className={styles.settingsCardTitle}>적합 항목 ({passItems.length}개)</h3>
                  <div className={styles.issueTable}>
                    <div className={styles.tableHeader}>
                      <div className={styles.colName}>품명</div>
                      <div className={styles.colPrice}>적용단가</div>
                      <div className={styles.colStatus}>분류</div>
                      <div className={styles.colReason}>인도조건</div>
                    </div>
                    {passItems.map((item: any, idx: number) => (
                      <div key={idx} className={styles.tableRow}>
                        <div className={styles.colName}>
                          {item.name || "(이름 없음)"}
                          {item.standard && (
                            <div className={styles.itemStandard}>{item.standard}</div>
                          )}
                          {item.product_id && (
                            <div className={styles.itemDivision}>식별번호: {item.product_id}</div>
                          )}
                        </div>
                        <div className={styles.colPrice}>
                          {item.apply_price ? Number(item.apply_price).toLocaleString() : "-"}
                        </div>
                        <div className={styles.colStatus}>
                          <span className={styles.govtTag}>{item.category}</span>
                        </div>
                        <div className={styles.colReason}>
                          {item.delivery_condition ? (
                            <span className={styles.reasonOneLiner}>{item.delivery_condition}</span>
                          ) : (
                            <span className={styles.reasonOneLiner}>-</span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </section>
          );
        })()}

        {/* 핵심 확인 사항 섹션 */}
        {verdict?.key_issues?.length > 0 && (
          <section ref={prioritySectionRef} className={styles.prioritySection}>
            <h2 className={styles.sectionTitle}>
              <span className={styles.sectionNumber}>{priorityNum}</span>
              핵심 확인 사항
            </h2>
            <div className={styles.priorityList}>
              {verdict.key_issues.map((issue: any, idx: number) => (
                <div key={idx} className={styles.priorityItem}>
                  <span className={`${styles.priorityBadge} ${getSeverityClass(issue.severity)}`}>
                    {SEVERITY_LABELS[issue.severity] || issue.severity}
                  </span>
                  <div className={styles.priorityContent}>
                    <div className={styles.priorityName}>
                      {issue.name}
                      {issue.standard && (
                        <span className={styles.priorityStandard}>{issue.standard}</span>
                      )}
                    </div>
                    <div className={styles.priorityDescription}>{issue.description}</div>
                    <div className={styles.priorityGuide}>
                      <span className={styles.guideLabel}>확인 방법</span>
                      {issue.guide}
                    </div>
                  </div>
                </div>
              ))}
              {verdict.total_key_issues > verdict.key_issues.length && (
                <div className={styles.priorityTruncation}>
                  전체 {verdict.total_key_issues}건 중 상위 {verdict.key_issues.length}건을 표시합니다. 상세 내용은 아래 검증 결과에서 확인하세요.
                </div>
              )}
            </div>
          </section>
        )}

        {/* 상세 검증 결과 섹션 */}
        <section ref={detailSectionRef} className={styles.detailSection}>
          <h2 className={styles.sectionTitle}>
            <span className={styles.sectionNumber}>{detailNum}</span>
            상세 검증 결과
          </h2>

          {results.map((projectResult, pIdx) => (
            <div key={pIdx} className={styles.projectBlock}>
              {results.length > 1 && (
                <h3 className={styles.projectName}>
                  {projectResult.project_name || `프로젝트 ${pIdx + 1}`}
                </h3>
              )}

              {Object.entries(CATEGORY_LABELS).map(([catKey, catLabel], catIdx) => {
                const items = projectResult[catKey] || [];
                const summary = projectResult[`${catKey}_summary`] || {};

                const problemItems = items.filter(
                  (item: any) => item.status !== "pass"
                );
                const passItems = items.filter(
                  (item: any) => item.status === "pass"
                );

                const blockIdx = pIdx * Object.keys(CATEGORY_LABELS).length + catIdx;

                return (
                  <div
                    key={catKey}
                    className={styles.categoryBlock}
                    ref={(el) => { categoryBlockRefs.current[blockIdx] = el; }}
                  >
                    <div className={styles.categoryHeader}>
                      <div className={styles.categoryInfo}>
                        <h4 className={styles.categoryName}>{catLabel}</h4>
                        <p className={styles.categoryDesc}>
                          {CATEGORY_DESCRIPTIONS[catKey]}
                        </p>
                      </div>
                      <div className={styles.categoryStats}>
                        <span className={styles.statsPass}>{summary.pass || 0}</span>
                        <span className={styles.statsSlash}>/</span>
                        <span className={styles.statsTotal}>{summary.total || 0}</span>
                      </div>
                    </div>

                    {/* 문제 항목 테이블 */}
                    {problemItems.length > 0 && (
                      <div className={styles.issueTable}>
                        <div className={styles.tableHeader}>
                          <div className={styles.colName}>항목</div>
                          <div className={styles.colPrice}>적용단가</div>
                          <div className={styles.colStatus}>결과</div>
                          <div className={styles.colReason}>사유</div>
                        </div>
                        {problemItems.map((item: any, iIdx: number) => {
                          const itemHasDetail = hasDetail(item);
                          const reasonText = item.description || item.reason || "";

                          return (
                          <React.Fragment key={iIdx}>
                          {/* 요약행 */}
                          <div className={styles.tableRow}>
                            <div className={styles.colName}>
                              {item.name || "(이름 없음)"}
                              {item.standard && (
                                <div className={styles.itemStandard}>{item.standard}</div>
                              )}
                              {catKey === "material_prices" && item.is_government && (
                                <>
                                  <span className={styles.govtTag}>{item.government_category}</span>
                                  {item.delivery_condition && (
                                    <div className={styles.itemDivision}>인도: {item.delivery_condition}</div>
                                  )}
                                </>
                              )}
                              {catKey === "material_prices" && item.division && (
                                <div className={styles.itemDivision}>{item.division}</div>
                              )}
                            </div>
                            <div className={styles.colPrice}>
                              {(catKey === "material_prices" || catKey === "labor_prices") && item.applyPrice !== undefined
                                ? Number(item.applyPrice).toLocaleString()
                                : "-"}
                            </div>
                            <div className={styles.colStatus}>
                              {(() => {
                                const badge = getActionBadge(item);
                                return (
                                  <span className={badge.className}>
                                    {badge.label}
                                  </span>
                                );
                              })()}
                            </div>
                            <div className={styles.colReason}>
                              <span className={styles.reasonOneLiner}>{reasonText}</span>
                            </div>
                          </div>
                          {/* 확장 상세행 */}
                          {itemHasDetail && (
                            <div className={styles.detailRow}>
                              <div className={styles.detailRowInner}>
                                {item.guide && item.severity !== "none" && (
                                  <div className={styles.guideText}>
                                    <span className={styles.guideLabel}>확인 방법</span>
                                    {item.guide}
                                  </div>
                                )}
                                {/* 노임단가: 비교 테이블 (date_context가 있을 때) */}
                                {item.date_context ? (
                                  <div className={styles.laborCompareWrap}>
                                    <p className={styles.laborCompareExplain}>{item.date_context.explanation}</p>
                                    <table className={styles.laborCompareTable}>
                                      <thead>
                                        <tr>
                                          <th>항목</th>
                                          <th>설계 내역<br/><span className={styles.laborDateSub}>{item.date_context.file_date} 기준</span></th>
                                          <th>현재 기준<br/><span className={styles.laborDateSub}>{item.date_context.db_date} 기준</span></th>
                                          <th>차이</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        <tr>
                                          <td className={styles.laborFieldCol}>적용일자</td>
                                          <td className={styles.laborValueCol}>{item.date_context.file_date}</td>
                                          <td className={styles.laborValueCol}>{item.date_context.db_date}</td>
                                          <td className={styles.laborDiffCol}>기준일 변경</td>
                                        </tr>
                                        {item.date_context.has_surcharge ? (
                                          <>
                                            <tr>
                                              <td className={styles.laborFieldCol}>기본단가</td>
                                              <td className={styles.laborValueCol}>{Number(item.date_context.file_base_price).toLocaleString()}</td>
                                              <td className={styles.laborValueCol}>{Number(item.date_context.db_price).toLocaleString()}</td>
                                              <td className={`${styles.laborDiffCol} ${item.date_context.base_diff > 0 ? styles.laborDiffPlus : item.date_context.base_diff < 0 ? styles.laborDiffMinus : ''}`}>
                                                {item.date_context.base_diff !== 0
                                                  ? `${item.date_context.base_diff > 0 ? '+' : ''}${item.date_context.base_diff.toLocaleString()}원`
                                                  : '일치'}
                                              </td>
                                            </tr>
                                            <tr>
                                              <td className={styles.laborFieldCol}>노임할증</td>
                                              <td className={styles.laborValueCol}>+{Number(item.date_context.surcharge_price).toLocaleString()} ({item.date_context.surcharge_rate}%)</td>
                                              <td className={styles.laborValueCol}>-</td>
                                              <td className={styles.laborDiffCol}>할증 적용</td>
                                            </tr>
                                            <tr className={styles.laborTotalRow}>
                                              <td className={styles.laborFieldCol}>적용단가 (합계)</td>
                                              <td className={styles.laborValueCol}>{Number(item.date_context.file_price).toLocaleString()}</td>
                                              <td className={styles.laborValueCol}>{Number(item.date_context.db_price).toLocaleString()}</td>
                                              <td className={`${styles.laborDiffCol} ${item.date_context.diff > 0 ? styles.laborDiffPlus : item.date_context.diff < 0 ? styles.laborDiffMinus : ''}`}>
                                                {item.date_context.diff !== 0
                                                  ? `${item.date_context.diff > 0 ? '+' : ''}${item.date_context.diff.toLocaleString()}원`
                                                  : '일치'}
                                              </td>
                                            </tr>
                                          </>
                                        ) : (
                                          <tr>
                                            <td className={styles.laborFieldCol}>적용단가</td>
                                            <td className={styles.laborValueCol}>{Number(item.date_context.file_price).toLocaleString()}</td>
                                            <td className={styles.laborValueCol}>{Number(item.date_context.db_price).toLocaleString()}</td>
                                            <td className={`${styles.laborDiffCol} ${item.date_context.diff > 0 ? styles.laborDiffPlus : item.date_context.diff < 0 ? styles.laborDiffMinus : ''}`}>
                                              {item.date_context.diff !== 0
                                                ? `${item.date_context.diff > 0 ? '+' : ''}${item.date_context.diff.toLocaleString()}원`
                                                : '일치'}
                                            </td>
                                          </tr>
                                        )}
                                      </tbody>
                                    </table>
                                  </div>
                                ) : item.price_context ? (
                                  <div className={styles.laborCompareWrap}>
                                    {item.price_context.explanation && (
                                      <p className={styles.laborCompareExplain}>{item.price_context.explanation}</p>
                                    )}
                                    <table className={styles.laborCompareTable}>
                                      <thead>
                                        <tr>
                                          <th>가격 소스</th>
                                          <th>설계 내역</th>
                                          <th>DB 기준</th>
                                          <th>차이</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {/* 소스별 비교: DB 소스 기준으로 매칭 */}
                                        {item.price_context.db_sources?.length > 0 ? (
                                          (() => {
                                            // DB 소스별로 파일 소스 매칭
                                            const fileMap: Record<string, any> = {};
                                            (item.price_context.file_sources || []).forEach((s: any) => {
                                              fileMap[s.kind] = s;
                                            });
                                            const shownKinds = new Set<string>();
                                            const rows: any[] = [];

                                            // DB에 있는 소스별 비교
                                            item.price_context.db_sources.forEach((dbS: any) => {
                                              const fileS = fileMap[dbS.kind];
                                              shownKinds.add(dbS.kind);
                                              const fp = fileS?.price || 0;
                                              const dp = dbS.price || 0;
                                              const d = fp - dp;
                                              rows.push(
                                                <tr key={dbS.kind}>
                                                  <td className={styles.laborFieldCol}>{dbS.kind}{dbS.page ? ` (p.${dbS.page})` : ''}</td>
                                                  <td className={styles.laborValueCol}>{fp > 0 ? fp.toLocaleString() : '-'}</td>
                                                  <td className={styles.laborValueCol}>{dp.toLocaleString()}</td>
                                                  <td className={`${styles.laborDiffCol} ${d > 0 ? styles.laborDiffPlus : d < 0 ? styles.laborDiffMinus : ''}`}>
                                                    {fp === 0 ? '미입력' : d !== 0 ? `${d > 0 ? '+' : ''}${d.toLocaleString()}원` : '일치'}
                                                  </td>
                                                </tr>
                                              );
                                            });

                                            // 파일에만 있는 소스 (추가된 것)
                                            (item.price_context.file_sources || []).forEach((fileS: any) => {
                                              if (!shownKinds.has(fileS.kind)) {
                                                rows.push(
                                                  <tr key={`file-${fileS.kind}`}>
                                                    <td className={styles.laborFieldCol}>{fileS.kind} <span className={styles.materialAddedBadge}>추가</span></td>
                                                    <td className={styles.laborValueCol}>{fileS.price.toLocaleString()}</td>
                                                    <td className={styles.laborValueCol}>-</td>
                                                    <td className={styles.laborDiffCol}>설계자 추가</td>
                                                  </tr>
                                                );
                                              }
                                            });

                                            return rows;
                                          })()
                                        ) : (
                                          // DB에 소스가 없는 경우 (견적단가 등)
                                          item.price_context.file_sources?.map((s: any, sIdx: number) => (
                                            <tr key={sIdx}>
                                              <td className={styles.laborFieldCol}>{s.kind || '견적'}</td>
                                              <td className={styles.laborValueCol}>{s.price.toLocaleString()}</td>
                                              <td className={styles.laborValueCol}>-</td>
                                              <td className={styles.laborDiffCol}>DB 없음</td>
                                            </tr>
                                          ))
                                        )}
                                        {/* 적용단가 합계행 */}
                                        <tr className={styles.laborTotalRow}>
                                          <td className={styles.laborFieldCol}>적용단가</td>
                                          <td className={styles.laborValueCol}>{Number(item.price_context.file_price).toLocaleString()}</td>
                                          <td className={styles.laborValueCol}>{item.price_context.db_price > 0 ? Number(item.price_context.db_price).toLocaleString() : '-'}</td>
                                          <td className={`${styles.laborDiffCol} ${item.price_context.diff > 0 ? styles.laborDiffPlus : item.price_context.diff < 0 ? styles.laborDiffMinus : ''}`}>
                                            {item.price_context.db_price > 0 && item.price_context.diff !== 0
                                              ? `${item.price_context.diff > 0 ? '+' : ''}${item.price_context.diff.toLocaleString()}원`
                                              : item.price_context.db_price > 0 ? '일치' : '비교 불가'}
                                          </td>
                                        </tr>
                                      </tbody>
                                    </table>
                                  </div>
                                ) : item.changes?.length > 0 ? (
                                  <div className={styles.changeList}>
                                    {item.changes.map((change: any, cIdx: number) => (
                                      <div key={cIdx} className={styles.changeItem}>
                                        <span className={styles.changeField}>{change.field}</span>
                                        <span className={styles.changeArrow}>
                                          <span className={styles.oldValue}>{formatValue(change.original)}</span>
                                          →
                                          <span className={styles.newValue}>{formatValue(change.file_value)}</span>
                                        </span>
                                        {change.impact_amount !== undefined && change.impact_amount !== 0 && (
                                          <span className={styles.impactAmount}>
                                            ({change.impact_amount > 0 ? "+" : ""}{change.impact_amount.toLocaleString()}원)
                                          </span>
                                        )}
                                      </div>
                                    ))}
                                  </div>
                                ) : null}
                                {item.content_checks?.length > 0 && (
                                  <div className={styles.contentChecks}>
                                    {item.content_checks.map((cc: any, ccIdx: number) => (
                                      <div key={ccIdx} className={styles.checkItem}>
                                        <span className={styles.checkType}>[{cc.type}]</span>
                                        <span className={styles.checkName}>{cc.name}</span>
                                        <span className={cc.result === "통과" ? styles.checkPass : styles.checkFail}>
                                          {cc.result}
                                        </span>
                                      </div>
                                    ))}
                                  </div>
                                )}
                                {item.added_items?.length > 0 && (
                                  <div className={item.note === '오염된 데이터' ? styles.contaminatedItems : styles.addedItems}>
                                    <div className={styles.addedItemsHeader}>
                                      {item.note === '오염된 데이터' ? '오염된 항목:' : '추가된 항목:'}
                                    </div>
                                    {item.added_items.map((ai: any, aiIdx: number) => (
                                      <div key={aiIdx} className={`${styles.addedItem} ${ai.contaminated ? styles.contaminated : ''}`}>
                                        <span className={styles.addedItemName}>
                                          {ai.ref_type ? `[${ai.ref_type}] ${ai.ref_name || ai.name}` : `[손료] ${ai.name}`}
                                        </span>
                                        {ai.count !== undefined && (
                                          <span className={styles.addedItemCount}>수량: {ai.count}</span>
                                        )}
                                      </div>
                                    ))}
                                  </div>
                                )}
                                {/* 기계경비 조건 검증 */}
                                {item.machine_checks?.length > 0 && (
                                  <div className={styles.machineChecksFullWidth}>
                                    <div className={styles.machineChecksHeader}>기계경비 조건 검증</div>
                                    {item.machine_checks.map((mc: any, mcIdx: number) => (
                                      <div key={mcIdx} className={styles.machineCheckItem}>
                                        <div className={styles.machineCheckTitle}>
                                          <span className={styles.machineName}>
                                            {mc.subtitle_name || mc.machine_name}
                                          </span>
                                          <span className={
                                            mc.overall_status === 'match' ? styles.machineStatusMatch :
                                            mc.overall_status === 'review_required' ? styles.machineStatusReview :
                                            mc.overall_status === 'needs_check' ? styles.machineStatusModified :
                                            mc.overall_status === 'no_template' ? styles.machineStatusNoTemplate :
                                            styles.machineStatusMatch
                                          }>
                                            {mc.overall_status === 'match' ? '조건 일치' :
                                             mc.overall_status === 'review_required' ? '검토 필요' :
                                             mc.overall_status === 'needs_check' ? '검토 필요' :
                                             mc.overall_status === 'no_template' ? '자동검증 불가' : '조건 일치'}
                                          </span>
                                        </div>
                                        {mc.template_found && mc.variable_checks?.some((vc: any) => !['user_input', 'no_value'].includes(vc.status)) && (
                                          <div className={styles.machineVarTable}>
                                            <div className={styles.machineVarHeader}>
                                              <span>조건 항목</span><span>적용값</span><span>해당 조건</span><span>판정</span>
                                            </div>
                                            {mc.variable_checks.filter((vc: any) => vc.status !== 'user_input' && vc.status !== 'no_value').map((vc: any, vcIdx: number) => {
                                              const hasFormula = vc.explain && /[\/\+\*\(\)]/.test(vc.explain) && vc.explain !== String(vc.file_value);
                                              const fc = vc.formula_context;
                                              const conditionText = vc.status === 'formula_derived' ? vc.condition_name :
                                                   vc.status === 'match' ? vc.condition_name :
                                                   vc.status === 'in_range' ? `${vc.condition_name} 범위 (대표값 ${vc.closest_value})` :
                                                   vc.status === 'custom_input' ? `직접입력 — ${vc.closest_condition}(${vc.closest_value})에 근접` :
                                                   vc.status === 'needs_check' ? (fc?.description || `조건표에 없음 — ${vc.closest_condition}(${vc.closest_value})에 근접`) :
                                                   vc.status === 'review_required' ? `조건표 범위(${vc.value_range}) 밖` :
                                                   vc.status === 'needs_verification' ? (vc.note || '설계자 입력값 — 확인 필요') : '-';
                                              const detailText = fc?.description;
                                              const showDetail = !!detailText && (vc.status === 'formula_derived' || vc.status === 'match' || vc.status === 'in_range');
                                              return (
                                              <React.Fragment key={vcIdx}>
                                              <div className={`${styles.machineVarRow} ${vc.status === 'review_required' || vc.status === 'needs_verification' ? styles.machineVarRowReview : ''} ${showDetail ? styles.machineVarRowHasDetail : ''}`}>
                                                <span className={styles.machineVarLabel}>{vc.label || vc.name}</span>
                                                <span className={styles.machineVarValue}>
                                                  {vc.file_value != null ? Number(vc.file_value.toFixed(4)) : (vc.explain || (vc.distance_details?.length ? '' : '-'))}
                                                  {hasFormula && <span className={styles.machineVarExplain}>{vc.explain}</span>}
                                                  {vc.distance_details?.length > 0 && (
                                                    <span className={styles.machineDistanceDetails}>
                                                      {vc.distance_details.map((d: any, di: number) => (
                                                        <span key={di} className={styles.machineDistanceItem}>
                                                          {d.name}={d.value}{d.unit ? d.unit : 'km'}{d.note ? ` (${d.note})` : ''}
                                                        </span>
                                                      ))}
                                                    </span>
                                                  )}
                                                </span>
                                                <span className={styles.machineConditionName}>{conditionText}</span>
                                                <span className={
                                                  vc.status === 'match' || vc.status === 'formula_derived' ? styles.machineVarMatch :
                                                  vc.status === 'in_range' ? styles.machineVarInRange :
                                                  vc.status === 'custom_input' ? styles.machineVarUserInput :
                                                  vc.status === 'review_required' ? styles.machineVarReview :
                                                  vc.status === 'needs_verification' ? styles.machineVarReview :
                                                  styles.machineVarNeedsCheck
                                                }>
                                                  {vc.status === 'match' || vc.status === 'formula_derived' ? '적합' :
                                                   vc.status === 'in_range' ? '적합' :
                                                   vc.status === 'custom_input' ? '직접입력' :
                                                   vc.status === 'needs_verification' ? '확인' :
                                                   vc.status === 'review_required' ? '검토' : '검토'}
                                                </span>
                                              </div>
                                              {showDetail && (
                                                <div className={styles.machineVarDetail}>
                                                  <span className={styles.machineVarDetailIcon}>↳</span> {detailText}
                                                </div>
                                              )}
                                              </React.Fragment>
                                              );
                                            })}
                                          </div>
                                        )}
                                        {mc.cross_validations?.length > 0 && (
                                          <div className={styles.machineCrossValidation}>
                                            {mc.cross_validations.map((cv: any, cvIdx: number) => (
                                              <div key={cvIdx} className={styles.machineCrossItem}>
                                                {cv.message}
                                              </div>
                                            ))}
                                          </div>
                                        )}
                                        {mc.calc_method_issue && (
                                          <div className={styles.machineCalcMethodWarn}>
                                            {mc.calc_method_issue}
                                          </div>
                                        )}
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                          </React.Fragment>
                          );
                        })}
                      </div>
                    )}

                    {/* 적합 항목 요약 */}
                    {passItems.length > 0 && (
                      <div className={styles.passedSummary}>
                        <span className={styles.checkIcon}>✓</span>
                        <span>{passItems.length}개 항목이 기준 데이터와 일치합니다.</span>
                      </div>
                    )}

                    {items.length === 0 && (
                      <div className={styles.emptyState}>
                        해당 카테고리에 검증 항목이 없습니다.
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ))}
        </section>

        {/* 조치 요청 사항 섹션 */}
        {hasActionItems && (
          <section ref={actionSectionRef} className={styles.settingsSection}>
            <h2 className={styles.sectionTitle}>
              <span className={styles.sectionNumber}>{actionNum}</span>
              조치 요청 사항
            </h2>

            {actionItems.map((group: any, gIdx: number) => {
              const priorityLabel =
                group.priority === "required" ? "필수 조치" :
                group.priority === "review" ? "확인 필요" : "참고";
              const priorityCls =
                group.priority === "required" ? styles.severityHigh :
                group.priority === "review" ? styles.severityMedium : styles.severityLow;

              return (
                <div key={gIdx} className={styles.settingsCard}>
                  <h3 className={styles.settingsCardTitle}>
                    {gIdx + 1}. {group.title}
                    <span className={priorityCls} style={{ marginLeft: 8, fontSize: 11, padding: "2px 8px", borderRadius: 3 }}>
                      {priorityLabel}
                    </span>
                    <span style={{ fontSize: 12, color: "#64748b", fontWeight: 400, marginLeft: 8 }}>
                      ({group.count}건)
                    </span>
                  </h3>
                  <p style={{ fontSize: 13, color: "#334155", lineHeight: 1.8, margin: "8px 0 12px" }}>
                    {group.template}
                  </p>
                  <div style={{ paddingLeft: 4 }}>
                    {group.items.map((item: any, iIdx: number) => (
                      <div key={iIdx} style={{ fontSize: 13, color: "#1e293b", padding: "3px 0", display: "flex", gap: 6 }}>
                        <span style={{ color: "#94a3b8" }}>·</span>
                        <span>
                          <strong>{item.name}</strong>
                          {item.standard && (
                            <span style={{ color: "#64748b", marginLeft: 4 }}>({item.standard})</span>
                          )}
                          <span style={{ color: "#94a3b8", marginLeft: 6, fontSize: 11 }}>[{item.category}]</span>
                          {item.detail && (
                            <div style={{ color: "#64748b", fontSize: 12, marginTop: 1 }}>{item.detail}</div>
                          )}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </section>
        )}

        {/* 범례 및 안내 */}
        <section ref={legendSectionRef} className={styles.legendSection}>
          <h2 className={styles.sectionTitle}>
            <span className={styles.sectionNumber}>{legendNum}</span>
            검증 기준 안내
          </h2>

          {/* 검증 결과 범례 */}
          <h3 className={styles.legendSubtitle}>검증 결과</h3>
          <div className={styles.legendGrid}>
            <div className={styles.legendItem}>
              <span className={styles.statusPass}>적합</span>
              <span className={styles.legendDesc}>표준품셈, 물가자료, 시중노임단가 등 품셈 기준데이터와 일치하는 항목입니다.</span>
            </div>
            <div className={styles.legendItem}>
              <span className={styles.statusIssue}>확인필요</span>
              <span className={styles.legendDesc}>기준데이터와 차이가 있거나, 출처 확인이 필요한 항목입니다. 사유를 확인하세요.</span>
            </div>
          </div>

          {/* 용어 사전 */}
          <h3 className={styles.legendSubtitle}>용어 안내</h3>
          <div className={styles.glossary}>
            {GLOSSARY_ITEMS.map((item, idx) => (
              <div key={idx} className={styles.glossaryItem}>
                <span className={styles.glossaryTerm}>{item.term}</span>
                <span className={styles.glossaryDef}>{item.definition}</span>
              </div>
            ))}
          </div>

          <div className={styles.disclaimer}>
            <p>
              ※ 본 검증은 데이터의 형식적 일치 여부를 확인한 것이며,
              단가의 적정성이나 수량의 정확성을 보증하지 않습니다.
            </p>
            <p>
              ※ '확인필요' 항목은 담당자의 추가 확인이 필요합니다.
            </p>
          </div>
        </section>

        {/* 푸터 */}
        <footer className={styles.reportFooter}>
          <div className={styles.footerLine} />
          <div className={styles.footerContent}>
            <span>콘엑스 설계내역 검증 시스템</span>
            <span>{currentDate} 생성</span>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default VerificationReport;
